import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { type SelectOptionDefinition } from '@mui/base/useSelect/useSelect.types';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { InputAdornmentComponent } from '@vakantiesnl/components/src/atoms/InputAdornment';
import { MobileFilterMenuWrapper } from '@vakantiesnl/components/src/molecules/MobileFilterMenuWrapper';
import { SelectFormField, type SelectFormFieldProps } from '@vakantiesnl/components/src/molecules/SelectFormField';
import { SelectFormFieldMultiRoom } from '@vakantiesnl/components/src/molecules/SelectFormField/MultiRoom/SelectFormFieldMultiRoom';
import { SelectFormField as OldSelectFormField } from '@vakantiesnl/components/src/molecules/SelectFormField/OldSelectFormField';
import { type TextfieldComponentProps } from '@vakantiesnl/components/src/molecules/TextField';
import { useParseDurationsFilterConfigLister } from '@vakantiesnl/services/src/hooks/filter-hooks';
import { DURATION_CONFIG_OPTIONS } from '@vakantiesnl/services/src/util/durations';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import { noop, parseInt, isEqual } from 'lodash';

import { useCustomStyles } from './TripFilterMenu.style';
import { SelectFormFieldCalendar } from '../SelectFormField/Calendar/SelectFormFieldCalendar';

export type TripFilterMenuProps = {
	microCopies: MicroCopy;
	partyString: string;
	airportOptions: SelectOptionDefinition<string>[] | null;
	mealplanOptions: SelectOptionDefinition<string>[] | null;
	onSetDepartureDate: (departureDate: string[]) => void;
	onChangeAirport: SelectFormFieldProps<string, true>['onChange'];
	onChangeDurationRange: SelectFormFieldProps<string, true>['onChange'];
	onPartyUpdate: (party: Search.PartyComposition[]) => void;
	onClosePartyPicker: (party: Search.PartyComposition[]) => void;
	onChangeMealplan: SelectFormFieldProps<string, true>['onChange'];
	durations: number[];
	departureDate?: string[] | null;
	airports?: string[];
	mealplans?: string[];
	party?: Search.PartyComposition[];
	hideMultiRoom?: boolean;
};

// Defaults to one, since the pax filter is always selected.
const DEFAULT_FILTER_COUNT = 1;

export const TripFilterMenuVariantB: FC<TripFilterMenuProps> = ({
	microCopies,
	partyString,
	airportOptions,
	mealplanOptions,
	onSetDepartureDate,
	onChangeAirport,
	onChangeDurationRange,
	onPartyUpdate,
	onClosePartyPicker,
	onChangeMealplan,
	departureDate,
	airports,
	durations,
	mealplans,
	party,
	hideMultiRoom,
}) => {
	const { classes } = useCustomStyles();

	const [filterCount, setFilterCount] = useState(DEFAULT_FILTER_COUNT);

	const onCloseMobileFilterMenuWrapper = useCallback(() => {
		let numberOfFiltersSelected = DEFAULT_FILTER_COUNT;

		if (departureDate) {
			numberOfFiltersSelected++;
		}

		if (durations && durations.length > 0 && !isEqual(durations, DURATION_CONFIG_OPTIONS.default.array)) {
			numberOfFiltersSelected++;
		}

		if (airports && airports.length > 0) {
			numberOfFiltersSelected++;
		}

		if (mealplans && mealplans.length > 0) {
			numberOfFiltersSelected++;
		}

		setFilterCount(numberOfFiltersSelected);
	}, [airports, departureDate, mealplans, durations]);

	const durationsInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="time" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	const airportsInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="airplane" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	const mealplansInputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="food" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	useEffect(() => {
		// Call initially to update when the page is loaded
		onCloseMobileFilterMenuWrapper();
	});

	const durationDayRangeLister = useParseDurationsFilterConfigLister(microCopies);

	const selectedDurations = useMemo(
		() => findDayRangeObject(durationDayRangeLister, durations),
		[durationDayRangeLister, durations],
	);

	return (
		<MobileFilterMenuWrapper
			microCopies={microCopies}
			drawerTitle={microCopies['filters.tripfilters.drawerTitle']}
			numberOfActiveFilters={filterCount}
			onClose={onCloseMobileFilterMenuWrapper}
			openButtonText={microCopies['common.filters']}
		>
			<div className={classes.filterMenuWrapper}>
				<div className={classes.filterMenu}>
					<SelectFormFieldCalendar
						departureDate={departureDate || null}
						setDepartureDate={onSetDepartureDate}
						setFlexDays={noop}
						flexDays={0}
						hasFlexibilityOptions={false}
						hasDrawerOnMobileTablet={true}
						inputLabel={microCopies['filters.departureDateLabelText']}
						dataCy="tripFilterDatePicker"
					/>
					<SelectFormField
						dataCy="tripFilterDuration"
						label={microCopies['common.duration']}
						variant="checkbox"
						placeholder={microCopies['filters.noPreference']}
						microCopies={microCopies}
						options={durationDayRangeLister}
						value={selectedDurations}
						drawerHeaderMicrocopy={microCopies['common.amount_of_days']}
						onChange={onChangeDurationRange}
						InputProps={durationsInputProps}
						multiple={true}
					/>
					{!!airportOptions?.length && airportOptions?.length > 0 && (
						<OldSelectFormField
							microCopies={microCopies}
							placeholder={microCopies['filters.noPreference']}
							label={microCopies['filters.departureAirport']}
							options={airportOptions}
							onChange={onChangeAirport}
							defaultValue={airports}
							variant="checkbox"
							multiple={true}
							InputProps={airportsInputProps}
						/>
					)}
					<SelectFormFieldMultiRoom
						label={microCopies['filters.partyComposition']}
						defaultValue={partyString}
						initialParty={party}
						onChange={onPartyUpdate}
						onClose={onClosePartyPicker}
						hideMultiRoom={hideMultiRoom}
					/>
					{!!mealplanOptions?.length && mealplanOptions?.length > 0 && (
						<OldSelectFormField
							microCopies={microCopies}
							placeholder={microCopies['filters.noPreference']}
							label={microCopies['common.mealplan']}
							defaultValue={mealplans}
							options={mealplanOptions}
							onChange={onChangeMealplan}
							variant="checkbox"
							multiple={true}
							InputProps={mealplansInputProps}
						/>
					)}
				</div>
			</div>
		</MobileFilterMenuWrapper>
	);
};

/**
 * Find matching duration ranges based on the passed days
 * @param {Array} dayRanges - Array with objecten die label en value (comma-separated string) bevatten
 * @param {Array} targetDays - Array van getallen waarvoor je de juiste objecten zoekt
 * @returns {Array|null} - Array van gevonden objecten of null als niets gevonden
 */
const findDayRangeObject = (dayRanges: SelectOptionDefinition<string>[], targetDays: number[]): string[] => {
	const targetSet = new Set(targetDays);
	const result = dayRanges.filter((range) => range.value.split(',').some((day) => targetSet.has(parseInt(day))));
	return result.length > 0 ? result.map(({ value }) => value) : ['6', '7', '8', '9', '10'];
};
